import { v4 } from 'uuid';
import type { DatasetOperationV20260601 } from '@cotera/api';
import { isInternalDatasetColumn } from '@cotera/sdk/core';
import {
  duckDbIdentifier,
  duckDbStringLiteral,
  duckDbValueLiteral,
  fromArrowRow,
  parseRowCount,
} from '@cotera/client/app/etc/duckdb';
import { datasetPreviewDuckDb } from '@cotera/client/app/etc/duckdb/preview-db';
import { toast } from '@cotera/client/app/components/ui';
import type {
  DataGridCellEdit,
  DataGridCellValue,
  DataGridColumnStats,
  DataGridFilter,
  DataGridRowId,
  DataGridSort,
} from '@cotera/client/app/components/ui/data-grid';
import type {
  DatasetArtifactMetadataOutput,
  TenantedClient,
} from '@cotera/client/app/etc/api/typed-client';
import type { DatasetOperationColumnType } from '../dataset-columns';
import { DATASET_PREVIEW_ORDER_COLUMN, duckDbColumnType } from './operations';
import {
  DATASET_PREVIEW_SOURCE_ALIAS,
  carriesPreviewOrder,
  datasetPreviewSourceSql,
  projectsDatasetColumns,
} from './source-sql';
import { previewWhereSql, sortSql } from './query-sql';
import {
  emptyDatasetRowIdCursor,
  openDatasetRowIdCursor,
  type DatasetRowIdCursor,
} from './row-id-cursor';
import { datasetColumnStats } from './stats';
import type { DuckDbLayerStack } from '@cotera/client/app/etc/duckdb/layers';
import type { DatasetPendingEdits } from './pending-edits';
import type { LiveOperationsTable } from './live-operations';
import type { DatasetPreviewRow } from './types';

/**
 * Only the one capability the write paths use. Narrow, following
 * {@link LiveOperationsClient} and {@link LiveOperationsTable}, so a spec can
 * hand over an object literal with a single `vi.fn()` rather than a whole
 * tenanted client.
 */
export type DatasetPreviewWriteClient = {
  resource: {
    dataset: {
      appendOperations: TenantedClient['resource']['dataset']['appendOperations'];
    };
  };
};

const getFirstColumnValue = (row: Record<string, unknown>): unknown =>
  Object.values(row)[0];

/**
 * How many rows one export query pulls back.
 *
 * Larger than a preview page because an export is throughput, not latency — but
 * bounded, so a wide dataset does not land its every row (the Arrow buffer, the
 * JS objects and the CSV text all at once) in the tab's memory. Paging the
 * export is the whole point: one page is read, handed out and dropped before
 * the next, where reading it whole held the entire result three times over.
 */
const DATASET_EXPORT_PAGE_SIZE = 10_000;

type DatasetPreviewGridPage = {
  rows: DatasetPreviewRow[];
  totalRows: number | null;
};

/**
 * A page-at-a-time reader over the rows an export would download.
 *
 * A cursor rather than an array for the same reason as {@link DatasetRowIdCursor}:
 * the caller writes each page out and moves on, so only one page need ever be
 * resident. Internal bookkeeping columns are already stripped on the way out.
 */
export type DatasetExportCursor = {
  /**
   * Yields export rows {@link DATASET_EXPORT_PAGE_SIZE} at a time, in the grid's
   * order, each row exactly once. The final page is short rather than padded,
   * and an empty result yields nothing at all.
   */
  batches: () => AsyncGenerator<Record<string, DataGridCellValue>[]>;
};

type DatasetPreviewQueryInput = {
  filters: DataGridFilter[];
  /**
   * The free-text term narrowing every column at once, or `''` for none.
   *
   * Carried beside the filters rather than folded into them because it is not
   * one: a filter names a column, and this names none — it is the same question
   * asked of all of them. Every read takes it for the same reason they all take
   * the filters — the row count, the header distributions and the export have
   * to describe the rows on screen, not the ones a reader has searched past.
   */
  search: string;
  customSelectSql: string;
};

export type DatasetPreviewGridAdapter = {
  key: string;
  datasetId: string;
  columns: DatasetArtifactMetadataOutput['schema']['columns'];
  getRowId: (row: DatasetPreviewRow) => DataGridRowId;
  loadPage: (
    input: DatasetPreviewQueryInput & {
      offset: number;
      limit: number;
      sorts: DataGridSort[];
    }
  ) => Promise<DatasetPreviewGridPage>;
  loadColumnStats: (
    input: DatasetPreviewQueryInput & { columnId: string }
  ) => Promise<DataGridColumnStats>;
  /**
   * A cursor over every row the grid is currently showing, unpaged, for export.
   *
   * Takes the same filters, sorts and ad-hoc query as {@link loadPage} so what
   * downloads is what the user is looking at — having filtered a dataset down,
   * exporting the whole of it back out would be a surprise. Internal
   * bookkeeping columns are dropped: they are ours, not the user's data.
   *
   * A cursor rather than an array because a whole-dataset export used to
   * materialise every row into JS at once — on top of the Arrow buffer it was
   * read from and the CSV string it was serialised into — which is exactly the
   * kind of transient the preview's memory budget cannot spend. See
   * {@link DatasetExportCursor}.
   */
  exportRows: (
    input: DatasetPreviewQueryInput & { sorts: DataGridSort[] }
  ) => DatasetExportCursor;
  /**
   * A cursor over the durable row ids of every row matching the given predicates.
   *
   * Deliberately not {@link exportRows}, which strips internal columns on the way
   * out — and the row id is exactly what it strips. This returns the identity and
   * nothing else, which is what a bulk action needs to name the rows it acts on.
   *
   * A cursor rather than an array because the caller is a dispatch loop, not a
   * reader: it consumes ids in small batches over a long stretch of wall clock,
   * and an array would pin the whole set in the tab for all of it. See
   * {@link DatasetRowIdCursor}.
   *
   * `extraWhereSql` is appended to the filter predicate rather than replacing it,
   * so a caller can narrow by a layer's projected columns — the wrapped source
   * makes those addressable exactly like the dataset's own.
   *
   * Empty when the dataset has no durable id column: without one there is nothing
   * stable to name a row by, and positions do not survive a re-query.
   */
  openRowIdCursor: (
    input: DatasetPreviewQueryInput & { extraWhereSql?: string }
  ) => Promise<DatasetRowIdCursor>;
  /**
   * One aggregate row over the same source the grid reads.
   *
   * Deliberately takes a select list rather than a shape: a caller summarising
   * its own projected columns knows what it wants counted, and the grid knows
   * only how to address them. Keeps layer-specific SQL in the layer.
   */
  aggregate: (
    input: DatasetPreviewQueryInput & { selectSql: string }
  ) => Promise<Record<string, unknown>>;
  /** False for dataset kinds that cannot be read through an ad-hoc SELECT. */
  queryable: boolean;
  /** Saved preview query, or the default when the dataset has none. */
  initialQuery: string;
  /** True when rows carry a durable id, which in-place editing requires. */
  editable: boolean;
  /** Reports a locally-applied query so the page can track unsaved state. */
  onQueryChanged: (query: string) => void;
  /**
   * Appends an empty row. The durable row id is allocated by the server so CSV
   * and manual datasets keep their own id conventions.
   */
  addRow: () => Promise<DatasetPreviewRow>;
  /** Adds a column to the dataset and to the local preview table. */
  addColumn: (input: {
    name: string;
    type: DatasetOperationColumnType;
  }) => Promise<void>;
  /** Renames a column in the dataset and in the local preview table. */
  renameColumn: (input: { oldName: string; newName: string }) => Promise<void>;
  /** Removes a column from the dataset and from the local preview table. */
  deleteColumn: (input: { name: string }) => Promise<void>;
  /**
   * Removes rows from the dataset and from the local preview table.
   *
   * Takes the whole set rather than one id at a time: rows are picked in the
   * grid by range, and appending one operation per row would mean N round trips
   * that can half-succeed, leaving the grid showing a deletion the dataset
   * never took.
   */
  deleteRows: (input: { rowIds: readonly DataGridRowId[] }) => Promise<void>;
  /** Applies an edit to the local DuckDB preview only. */
  applyCellEdit: (input: {
    rowId: DataGridRowId;
    columnName: string;
    value: DataGridCellValue;
  }) => Promise<void>;
  /** Persists edits to the dataset as operations. */
  saveCellEdits: (edits: DataGridCellEdit[]) => Promise<void>;
};

/**
 * The overlay layers (the automations column and its runs join) only make sense
 * over the full dataset, keyed by its internal row-id column. An ad-hoc query
 * that reshapes the rows — an aggregate, or a narrowed projection, which is what
 * Coco writes when asked to query a dataset — drops that internal column, so
 * wrapping it with the automations `LEFT JOIN ... ON cotera_src_N."__cotera_...
 * row_number"` would reference a column the source no longer has and DuckDB
 * rejects the whole read ("does not have a column named __cotera_csv_row_number").
 *
 * Over such a query the overlay is dropped — its join is suppressed and its
 * columns leave the sortable/stats set — exactly as {@link carriesPreviewOrder}
 * and {@link projectsDatasetColumns} already drop ordering and column-aware
 * predicates for the same reshaped queries. `projectsDatasetColumns` is true
 * only for `SELECT * FROM dataset ...`, which keeps every column the CTE has,
 * so whenever the overlay is kept its row-id column is guaranteed present.
 */
export const overlayWrapSource = (
  layers: DuckDbLayerStack<DatasetPreviewRow>,
  customSelectSql: string
): ((baseSql: string) => string) | undefined =>
  projectsDatasetColumns(customSelectSql) ? layers.wrapSource : undefined;

export const overlayColumns = (
  layers: DuckDbLayerStack<DatasetPreviewRow>,
  customSelectSql: string
): DuckDbLayerStack<DatasetPreviewRow>['projectedColumns'] =>
  projectsDatasetColumns(customSelectSql) ? layers.projectedColumns : [];

export const createDuckDbDatasetPreviewGridAdapter = ({
  key,
  columns,
  tableName,
  datasetId,
  getBaseArtifactId,
  rowIdColumn,
  queryable,
  readOnly,
  initialQuery,
  onQueryChanged,
  onColumnAdded,
  onColumnRenamed,
  onColumnRemoved,
  layers,
  pendingEdits,
  previewTable,
  markApplied,
  client,
}: {
  key: string;
  columns: DatasetArtifactMetadataOutput['schema']['columns'];
  tableName: string;
  datasetId: string;
  /**
   * The artifact an edit declares it was built against, read at write time
   * rather than captured.
   *
   * A getter because compaction republishes the dataset's artifact while the
   * grid is open: capturing the id would both send a superseded one — which the
   * server rejects the batch on — and make the adapter's identity move with
   * every compaction, rebuilding the whole view model for a write path that
   * only reads it.
   */
  getBaseArtifactId: () => string | null;
  rowIdColumn: string | null;
  queryable: boolean;
  /** Viewer-only surfaces (the trigger picker) never write to the dataset. */
  readOnly: boolean;
  initialQuery: string;
  onQueryChanged: (query: string) => void;
  onColumnAdded: (column: { name: string; type: string }) => void;
  onColumnRenamed: (change: { oldName: string; newName: string }) => void;
  onColumnRemoved: (column: { name: string }) => void;
  /** The composed layer stack: its SQL wrapping and its extra columns. */
  layers: DuckDbLayerStack<DatasetPreviewRow>;
  /** Where an unsaved edit is recorded so a rebuild can replay it. */
  pendingEdits: DatasetPendingEdits;
  /**
   * The preview table itself, not just its name, so local writes go through
   * `.apply()` and take the same per-table lock the replay does — rather than
   * an unlocked INSERT racing a materialization in flight.
   */
  previewTable: LiveOperationsTable;
  /**
   * Moves the live-sync high-water mark past a sequence number this path just
   * wrote. The same POST that appended the operation broadcasts `ResourceUpdated`,
   * which wakes the live sync; without this it re-applies the insert and the row
   * lands twice.
   */
  markApplied: (sequenceNumbers: readonly number[]) => void;
  client: DatasetPreviewWriteClient;
}): DatasetPreviewGridAdapter => ({
  key,
  datasetId,
  columns,
  // Prefer the durable id: it survives re-queries, so memoized rows keep their
  // identity across a refresh and edits address a real row rather than a
  // position in the current page.
  getRowId: (row) => {
    if (rowIdColumn === null) {
      return row.__coteraRowId;
    }
    const value = row[rowIdColumn];
    return value === null || value === undefined
      ? row.__coteraRowId
      : String(value);
  },
  async loadPage({ offset, limit, sorts, filters, search, customSelectSql }) {
    const sourceSql = datasetPreviewSourceSql(
      tableName,
      customSelectSql,
      overlayWrapSource(layers, customSelectSql)
    );
    // A layer's columns are addressable by sorts and filters exactly like the
    // dataset's own, because the wrapped source makes them real columns.
    const sortableColumns = [
      ...columns,
      ...overlayColumns(layers, customSelectSql),
    ];
    const whereSql = previewWhereSql({
      filters,
      search,
      projectsKnownColumns: projectsDatasetColumns(customSelectSql),
      columns: sortableColumns,
    });
    // One predicate drives both: the column can only be ordered by, or
    // excluded, when the source actually still has it. `EXCLUDE` errors on a
    // column that is not there, so a user query that dropped it must not try.
    const ordered = carriesPreviewOrder(customSelectSql);
    const orderSql = sortSql(sorts, sortableColumns, ordered);
    const db = await datasetPreviewDuckDb;
    const [table, countTable] = await Promise.all([
      db.rawQuery(
        // The order column rides along so the ORDER BY can reference it, then
        // is dropped here rather than in the source — it is bookkeeping, not
        // data, and nothing downstream should see it.
        `SELECT ${
          ordered
            ? `* EXCLUDE (${duckDbIdentifier(DATASET_PREVIEW_ORDER_COLUMN)})`
            : '*'
        } FROM (SELECT * FROM ${sourceSql} AS ${duckDbIdentifier(
          DATASET_PREVIEW_SOURCE_ALIAS
        )}${whereSql}${orderSql} LIMIT ${limit} OFFSET ${offset})`
      ),
      db.rawQuery(
        `SELECT count(*) AS row_count FROM ${sourceSql} AS ${duckDbIdentifier(
          DATASET_PREVIEW_SOURCE_ALIAS
        )}${whereSql}`
      ),
    ]);
    const rows = table.toArray().map((row, index) => ({
      ...fromArrowRow(row),
      __coteraRowId: offset + index,
    }));
    return {
      rows,
      totalRows: parseRowCount(
        getFirstColumnValue(countTable.toArray()[0] ?? {})
      ),
    };
  },
  exportRows({ sorts, filters, search, customSelectSql }) {
    const sourceSql = datasetPreviewSourceSql(
      tableName,
      customSelectSql,
      overlayWrapSource(layers, customSelectSql)
    );
    const sortableColumns = [
      ...columns,
      ...overlayColumns(layers, customSelectSql),
    ];
    const whereSql = previewWhereSql({
      filters,
      search,
      projectsKnownColumns: projectsDatasetColumns(customSelectSql),
      columns: sortableColumns,
    });
    const ordered = carriesPreviewOrder(customSelectSql);
    const orderSql = sortSql(sorts, sortableColumns, ordered);
    return {
      async *batches() {
        const db = await datasetPreviewDuckDb;
        let offset = 0;
        // Paged the same way the grid pages, and stable for the same reason: the
        // ordered source gives LIMIT/OFFSET a fixed order to walk, so no row is
        // read twice and none is skipped. One page is materialised — Arrow
        // buffer and JS objects both — and dropped before the next is read.
        for (;;) {
          const table = await db.rawQuery(
            `SELECT * FROM ${sourceSql} AS ${duckDbIdentifier(
              DATASET_PREVIEW_SOURCE_ALIAS
            )}${whereSql}${orderSql} LIMIT ${DATASET_EXPORT_PAGE_SIZE} OFFSET ${offset}`
          );
          const rows = table.toArray().map((row) => {
            const normalized = fromArrowRow(row);
            return Object.fromEntries(
              Object.entries(normalized).filter(
                ([name]) => !isInternalDatasetColumn(name)
              )
            );
          });
          if (rows.length === 0) {
            return;
          }
          yield rows;
          // A short page is the last page: the source had fewer rows left than
          // one page holds, so there is nothing after it to ask for.
          if (rows.length < DATASET_EXPORT_PAGE_SIZE) {
            return;
          }
          offset += rows.length;
        }
      },
    };
  },
  async openRowIdCursor({ filters, search, customSelectSql, extraWhereSql }) {
    if (rowIdColumn === null) {
      return emptyDatasetRowIdCursor();
    }
    const sourceSql = datasetPreviewSourceSql(
      tableName,
      customSelectSql,
      overlayWrapSource(layers, customSelectSql)
    );
    const whereSql = previewWhereSql({
      filters,
      search,
      projectsKnownColumns: projectsDatasetColumns(customSelectSql),
      columns: [...columns, ...overlayColumns(layers, customSelectSql)],
    });
    // Appended rather than merged into `previewWhereSql`: that one translates
    // the grid's own filters and search, and this is a caller's own predicate
    // over the same source.
    const extra =
      extraWhereSql === undefined || extraWhereSql === ''
        ? ''
        : whereSql === ''
        ? ` WHERE ${extraWhereSql}`
        : ` AND (${extraWhereSql})`;
    const db = await datasetPreviewDuckDb;
    // No LIMIT and no ORDER BY: the caller wants the set, not a page of it, and
    // the cursor supplies its own paging key over the snapshot this fills.
    return await openDatasetRowIdCursor({
      db,
      selectSql: `SELECT CAST(${duckDbIdentifier(
        rowIdColumn
      )} AS VARCHAR) AS row_id FROM ${sourceSql} AS ${duckDbIdentifier(
        DATASET_PREVIEW_SOURCE_ALIAS
      )}${whereSql}${extra}`,
    });
  },
  async aggregate({ filters, search, customSelectSql, selectSql }) {
    const sourceSql = datasetPreviewSourceSql(
      tableName,
      customSelectSql,
      overlayWrapSource(layers, customSelectSql)
    );
    const whereSql = previewWhereSql({
      filters,
      search,
      projectsKnownColumns: projectsDatasetColumns(customSelectSql),
      columns: [...columns, ...overlayColumns(layers, customSelectSql)],
    });
    const db = await datasetPreviewDuckDb;
    const table = await db.rawQuery(
      `SELECT ${selectSql} FROM ${sourceSql} AS ${duckDbIdentifier(
        DATASET_PREVIEW_SOURCE_ALIAS
      )}${whereSql}`
    );
    const row = table.toArray()[0];
    return row === undefined ? {} : fromArrowRow(row);
  },
  async loadColumnStats({ columnId, filters, search, customSelectSql }) {
    // The overlay columns only exist in the result when the query keeps them,
    // so they are only summarisable then — a reshaped query drops both the
    // columns and the join that would compute them.
    const overlay = overlayColumns(layers, customSelectSql);
    const statsColumns = [...columns, ...overlay];
    const column = statsColumns.find(
      (candidate) => candidate.name === columnId
    );
    if (column === undefined) {
      return {
        kind: 'textSummary',
        uniqueCount: 0,
        nullCount: 0,
        totalCount: 0,
      };
    }
    // Stats respect the active filters and ad-hoc query so the header
    // distribution always describes the rows the grid is showing. Sorting is
    // deliberately ignored — it cannot change the population.
    const whereSql = previewWhereSql({
      filters,
      search,
      projectsKnownColumns: projectsDatasetColumns(customSelectSql),
      columns: statsColumns,
    });
    const sourceSql = `(SELECT * FROM ${datasetPreviewSourceSql(
      tableName,
      customSelectSql,
      overlayWrapSource(layers, customSelectSql)
    )} AS ${duckDbIdentifier(DATASET_PREVIEW_SOURCE_ALIAS)}${whereSql})`;
    // A column that declares how to summarise itself wins; everything else
    // falls through to the type-derived default.
    const projected = overlay.find((candidate) => candidate.name === columnId);
    if (projected?.loadStats !== undefined) {
      return await projected.loadStats({ sourceSql });
    }
    return datasetColumnStats({
      sourceSql,
      columnName: columnId,
      columnType: column.type,
    });
  },
  queryable,
  initialQuery,
  editable: !readOnly && rowIdColumn !== null,
  onQueryChanged,
  async addRow() {
    if (rowIdColumn === null) {
      toast.error('This dataset does not have a durable row id column yet');
      throw new Error('Dataset has no durable row id column');
    }
    // The row id comes back from the server rather than being invented here:
    // CSV datasets key rows by row number and manual ones by uuid, and only the
    // server knows which convention this dataset uses.
    const result = await client.resource.dataset.appendOperations({
      id: datasetId,
      baseArtifactId: getBaseArtifactId(),
      operations: [
        {
          clientOperationId: v4(),
          op: {
            type: 'insert_row',
            values: {},
          } satisfies DatasetOperationV20260601,
        },
      ],
    });
    if (result.isErr()) {
      toast.error('Could not add row');
      throw new Error('Could not add row');
    }
    const appended = result.value.operations[0];
    const rowId = appended?.rowId;
    if (appended === undefined || rowId === undefined) {
      toast.error('Could not add row');
      throw new Error('Insert did not return a row id');
    }

    // Claimed before the write: this same POST triggered a `ResourceUpdated`
    // broadcast, and an insert the live sync has not been told about is applied
    // a second time. Marked *before* the apply so a broadcast that lands mid
    // apply finds the mark already moved.
    markApplied([appended.sequenceNumber]);

    // Routed through the preview table rather than a hand-written INSERT: the
    // statement is generated by the same replay the live sync uses, so the
    // fractional ordering comes from `previewRowInsertOrderExpression` instead
    // of a second copy, and it runs under `ParquetBackedTable`'s lock instead of
    // racing a materialization in flight.
    try {
      await previewTable.apply({
        operations: [{ type: 'insert_row', rowId, values: {} }],
        columns,
      });
    } catch (error) {
      toast.error('Could not add row');
      throw error;
    }

    const row: DatasetPreviewRow = {
      ...Object.fromEntries(columns.map((column) => [column.name, null])),
      [rowIdColumn]: rowId,
      // Only consulted when a dataset has no durable id column, which this
      // path has already ruled out.
      __coteraRowId: -1,
    };
    return row;
  },
  async addColumn({ name, type }) {
    const result = await client.resource.dataset.appendOperations({
      id: datasetId,
      baseArtifactId: getBaseArtifactId(),
      operations: [
        {
          clientOperationId: v4(),
          op: {
            type: 'add_column',
            columnName: name,
            columnType: type,
          } satisfies DatasetOperationV20260601,
        },
      ],
    });
    if (result.isErr()) {
      const message =
        result.error.t === 'application'
          ? result.error.err.message ?? 'Could not add column'
          : 'Could not add column';
      toast.error(message);
      throw new Error(message);
    }

    const appended = result.value.operations[0];
    if (appended !== undefined) {
      // Marked before the apply for the same reason as `addRow`: this POST's
      // `ResourceUpdated` broadcast wakes the live sync, and an unmarked
      // `add_column` makes `datasetOperationPatches` return `needsRebuild`,
      // forcing a full parquet re-download and view-model rebuild for the grid's
      // own column add.
      markApplied([appended.sequenceNumber]);
    }

    // Routed through the preview table rather than a hand-written ALTER: the
    // replay's statement is `ADD COLUMN IF NOT EXISTS`, so it is idempotent
    // where the raw one errored on a re-run, and it runs under the same lock.
    try {
      await previewTable.apply({
        operations: [
          { type: 'add_column', columnName: name, columnType: type },
        ],
        columns,
      });
    } catch (error) {
      toast.error('Could not add column');
      throw error;
    }
    onColumnAdded({ name, type: duckDbColumnType(type) });
  },
  async renameColumn({ oldName, newName }) {
    const result = await client.resource.dataset.appendOperations({
      id: datasetId,
      baseArtifactId: getBaseArtifactId(),
      operations: [
        {
          clientOperationId: v4(),
          op: {
            type: 'rename_column',
            oldColumnName: oldName,
            newColumnName: newName,
          } satisfies DatasetOperationV20260601,
        },
      ],
    });
    if (result.isErr()) {
      const message =
        result.error.t === 'application'
          ? result.error.err.message ?? 'Could not rename column'
          : 'Could not rename column';
      toast.error(message);
      throw new Error(message);
    }

    // The values are the user's own unsaved work and the column still exists,
    // so the edits move with it rather than being dropped the way a delete
    // drops them. Done before the local apply, so a throw there still leaves
    // the edits addressed to the name the schema is about to carry.
    pendingEdits.renameColumn(oldName, newName);

    const appended = result.value.operations[0];
    if (appended !== undefined) {
      // Marked before the apply for the same reason as `addColumn`: this POST's
      // `ResourceUpdated` broadcast wakes the live sync, and an unmarked
      // `rename_column` makes `datasetOperationPatches` return `needsRebuild`,
      // forcing a full parquet re-download and view-model rebuild for the
      // grid's own rename.
      markApplied([appended.sequenceNumber]);
    }

    // Routed through the preview table rather than a hand-written ALTER, as
    // every other local schema write is: the statement comes from the same
    // replay the live sync uses, and it runs under the table's lock.
    try {
      await previewTable.apply({
        operations: [
          {
            type: 'rename_column',
            oldColumnName: oldName,
            newColumnName: newName,
          },
        ],
        columns,
      });
    } catch (error) {
      toast.error('Could not rename column');
      throw error;
    }
    onColumnRenamed({ oldName, newName });
  },
  async deleteColumn({ name }) {
    const result = await client.resource.dataset.appendOperations({
      id: datasetId,
      baseArtifactId: getBaseArtifactId(),
      operations: [
        {
          clientOperationId: v4(),
          op: {
            type: 'delete_column',
            columnName: name,
          } satisfies DatasetOperationV20260601,
        },
      ],
    });
    if (result.isErr()) {
      const message =
        result.error.t === 'application'
          ? result.error.err.message ?? 'Could not delete column'
          : 'Could not delete column';
      toast.error(message);
      throw new Error(message);
    }

    // The column is gone server-side now, so any unsaved edit still queued
    // against it is dead. Cleared here rather than after the local apply so it
    // goes even if that throws, and before any rebuild can replay a stale edit
    // onto a column of the same name added later.
    pendingEdits.removeColumn(name);

    const appended = result.value.operations[0];
    if (appended !== undefined) {
      // Marked before the apply for the same reason as `addColumn`: this POST's
      // `ResourceUpdated` broadcast wakes the live sync, and an unmarked
      // `delete_column` makes `datasetOperationPatches` return `needsRebuild`,
      // forcing a full parquet re-download and view-model rebuild for the grid's
      // own column drop.
      markApplied([appended.sequenceNumber]);
    }

    // Routed through the preview table rather than a hand-written ALTER: the
    // replay's statement is `DROP COLUMN IF EXISTS`, so it is idempotent where
    // the raw one errored on a re-run, and it runs under the same lock.
    try {
      await previewTable.apply({
        operations: [{ type: 'delete_column', columnName: name }],
        columns,
      });
    } catch (error) {
      toast.error('Could not delete column');
      throw error;
    }
    onColumnRemoved({ name });
  },
  async deleteRows({ rowIds }) {
    if (rowIdColumn === null) {
      toast.error('This dataset does not have a durable row id column yet');
      throw new Error('Dataset has no durable row id column');
    }
    if (rowIds.length === 0) {
      return;
    }
    const ids = rowIds.map((rowId) => String(rowId));

    const result = await client.resource.dataset.appendOperations({
      id: datasetId,
      baseArtifactId: getBaseArtifactId(),
      operations: ids.map((rowId) => ({
        clientOperationId: v4(),
        op: { type: 'delete_row', rowId } satisfies DatasetOperationV20260601,
      })),
    });
    if (result.isErr()) {
      const message =
        result.error.t === 'application'
          ? result.error.err.message ?? 'Could not delete rows'
          : 'Could not delete rows';
      toast.error(message);
      throw new Error(message);
    }

    // The rows are gone server-side now, so any unsaved edit still queued
    // against one is dead. Cleared for the same reason `deleteColumn` clears
    // its column's: a CSV dataset keys rows by row number, so an id freed here
    // can be handed to a later insert, and a surviving edit would write a value
    // into a row the user never touched.
    pendingEdits.removeRows(ids);

    // Marked before the apply for the same reason as `deleteColumn`: this
    // POST's `ResourceUpdated` broadcast wakes the live sync, and re-applying a
    // delete the grid has already made is a no-op that still costs a rebuild.
    markApplied(
      result.value.operations.map((operation) => operation.sequenceNumber)
    );

    // Routed through the preview table rather than a hand-written DELETE, as
    // every other local write is: the statement comes from the same replay the
    // live sync uses, and it runs under `ParquetBackedTable`'s lock.
    try {
      await previewTable.apply({
        operations: ids.map((rowId) => ({
          type: 'delete_row' as const,
          rowId,
        })),
        columns,
      });
    } catch (error) {
      toast.error('Could not delete rows');
      throw error;
    }
  },
  async applyCellEdit({ rowId, columnName, value }) {
    if (rowIdColumn === null) {
      toast.error('This dataset does not have a durable row id column yet');
      return;
    }
    // Recorded before it is written, so a materialization that lands between
    // the two still replays it. Without this the table is rebuilt from the
    // parquet and the edit disappears with no error at all.
    pendingEdits.set({ rowId: String(rowId), columnName, value });
    // Local only: writing to DuckDB is what makes the edit visible to sorting,
    // filtering and the header stats. Persisting happens on "Save changes".
    const db = await datasetPreviewDuckDb;
    await db.rawQuery(
      `UPDATE ${duckDbIdentifier(tableName)}
       SET ${duckDbIdentifier(columnName)} = ${duckDbValueLiteral(value)}
       WHERE CAST(${duckDbIdentifier(
         rowIdColumn
       )} AS VARCHAR) = ${duckDbStringLiteral(String(rowId))}`
    );
  },
  async saveCellEdits(edits) {
    if (edits.length === 0) {
      return;
    }
    const result = await client.resource.dataset.appendOperations({
      id: datasetId,
      baseArtifactId: getBaseArtifactId(),
      operations: edits.map((edit) => ({
        clientOperationId: v4(),
        op: {
          type: 'update_cell',
          rowId: String(edit.rowId),
          columnName: edit.columnId,
          value: edit.value,
        } satisfies DatasetOperationV20260601,
      })),
    });
    if (result.isErr()) {
      // Left pending on purpose: the edit is still only in this browser, so it
      // still has to survive the next rebuild.
      toast.error('Could not save dataset edits');
      throw new Error('Could not save dataset edits');
    }
    // The operation log carries them now, so the replay will reproduce them.
    pendingEdits.remove(
      edits.map((edit) => ({
        rowId: String(edit.rowId),
        columnName: edit.columnId,
      }))
    );
  },
});
